// src/modules/moduleDashboards/constants.js
// Data-scope levels for the Module Dashboards, plus a hook that loads the live
// dashboard list.
//
// Dashboards are no longer a fixed catalog: every role permission set (scope
// 'role') owns one, so the list is fetched from the server rather than hardcoded
// here. Use useDashboardOptions() wherever a "which dashboard?" select is needed.
import { useEffect, useMemo, useState } from 'react';
import { fetchDashboards } from './api';

export const SCOPE_OPTIONS = [
  { value: 'BRANCH', label: 'Branch', rank: 1 },
  { value: 'AREA', label: 'Area (Region)', rank: 2 },
  { value: 'GLOBAL', label: 'Global', rank: 3 },
];

export const SCOPE_RANK = { BRANCH: 1, AREA: 2, GLOBAL: 3 };

// Scopes allowed given a maximum (inclusive, narrowest → maxScope).
export const scopesUpTo = (maxScope) => {
  const max = SCOPE_RANK[maxScope] || 1;
  return SCOPE_OPTIONS.filter((s) => s.rank <= max);
};

// Live { value, label } options for a dashboard select — one entry per role
// permission set. Fails soft to an empty list so a select never breaks a page.
//
// Pass the currently-bound key: if it no longer matches any role dashboard (a
// stale binding left over from the fixed-catalog days, or a deleted role) it is
// appended as a flagged option so the select shows what's actually stored instead
// of silently rendering a bare key.
export const useDashboardOptions = (currentKey) => {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchDashboards()
      .then((list) => { if (alive) setDashboards(list); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const options = useMemo(() => {
    const opts = dashboards.map((d) => ({
      value: d.key,
      label: d.hasLayout ? d.label : `${d.label} (no layout yet)`,
    }));
    if (!loading && currentKey && !dashboards.some((d) => d.key === currentKey)) {
      opts.push({ value: currentKey, label: `${currentKey} — unknown dashboard` });
    }
    return opts;
  }, [dashboards, currentKey, loading]);

  // The role set that owns a given dashboard key, for read-only displays.
  const byKey = useMemo(
    () => dashboards.reduce((acc, d) => { acc[d.key] = d; return acc; }, {}),
    [dashboards]
  );

  return { options, loading, dashboards, byKey };
};
